"use client";

/**
 * The System Card release map (LEV-2912): one lane per lab on a shared date
 * axis, closed labs above the line and open-weight labs below, with weights,
 * type and range filters, a detail panel and the latest 12 releases. The open
 * vs closed gap chart is left out until its readings carry verified sources.
 *
 * Dots are real `<button>`s laid over a decorative SVG (grid, lanes, labels),
 * so keyboard and screen-reader users get native buttons instead of
 * `role="button"` SVG groups.
 *
 * Filter state lives in the URL query (`?weights=&type=&range=`) so a filtered
 * view can be linked. It is read from `window.location` in an effect, never
 * from server `searchParams`: the edition pages are statically prerendered,
 * so the first render MUST be the default view, identical on server and client.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import type { ModelRelease } from "@/constants/systemCard.types";
import { trackLearnReleaseMapFilter } from "@/lib/analytics/client";
import {
  DEFAULT_RELEASE_MAP_FILTERS,
  RANGE_OPTIONS,
  RELEASE_LANE_COLORS,
  WEIGHTS_OPTIONS,
  countReleases,
  dropUnknownLanes,
  filterReleases,
  formatMonthTick,
  formatReleaseDate,
  formatShortReleaseDate,
  latestReleases,
  releaseVendor,
  collisionOffsets,
  majorLabel,
  makeDateScale,
  monthTicks,
  orderedLanes,
  parseIsoDate,
  parseReleaseMapFilters,
  rangeStart,
  typeLabel,
  typeOptions,
  writeReleaseMapFilters,
  type ReleaseMapFilters,
} from "@/lib/learn/releaseMap";

export interface ReleaseMapProps {
  releases: ModelRelease[];
  /** ISO date the release data was last updated. */
  updatedOn: string;
}

const WIDTH = 1150;
const PLOT_LEFT = 150;
const PLOT_RIGHT = 1120;
const TOP = 34;
const LANE_HEIGHT = 38;
const BAND_GAP = 26;
const LATEST_COUNT = 12;

const MUTED = "#717B8E";
const LINE = "#e7e1dc";
const GRID = "#f1ece7";

const CHIP_BASE =
  "rounded-full border px-3 py-1 text-[13px] font-semibold transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lev-orange focus-visible:ring-offset-1";
const CHIP_ON = "border-landing-dark bg-landing-dark text-white";
const CHIP_OFF = "border-gray-200 bg-white text-landing-dark hover:bg-gray-50";
const PANEL = "min-w-0 rounded-md border border-gray-200 bg-white px-4 py-4";
const EYEBROW = "text-[11px] font-semibold uppercase tracking-[0.12em] text-landing-secondary";

function releaseLabel(r: ModelRelease): string {
  return `${r.m}, ${r.lane}, ${formatReleaseDate(r.d)}${r.pending ? ", weights pending" : ""}`;
}

/** Solid for closed, ring for open, dashed ring for announced-but-unreleased. */
function dotStyle(r: ModelRelease, size: number): CSSProperties {
  const color = RELEASE_LANE_COLORS[r.lane];
  return {
    width: size,
    height: size,
    background: r.open || r.pending ? "transparent" : color,
    border: `${r.open ? 2 : 1.5}px ${r.pending ? "dashed" : "solid"} ${color}`,
  };
}

function FilterGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1.5">
      <span className={`${EYEBROW} mr-1`}>{label}</span>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
          className={`${CHIP_BASE} ${o.value === value ? CHIP_ON : CHIP_OFF}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ReleaseMap({ releases, updatedOn }: ReleaseMapProps) {
  const valid = useMemo(() => dropUnknownLanes(releases), [releases]);
  const types = useMemo(() => typeOptions(valid), [valid]);
  const [filters, setFilters] = useState<ReleaseMapFilters>(DEFAULT_RELEASE_MAP_FILTERS);
  const [selected, setSelected] = useState<ModelRelease | null>(() => {
    const majors = valid.filter((r) => r.major && !r.pending);
    return latestReleases(majors.length ? majors : valid, 1)[0] ?? null;
  });
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fromUrl = parseReleaseMapFilters(new URLSearchParams(window.location.search), types);
    setFilters((current) =>
      current.weights === fromUrl.weights && current.type === fromUrl.type && current.range === fromUrl.range
        ? current
        : fromUrl,
    );
  }, [types]);

  useEffect(() => {
    const box = scrollRef.current;
    if (box) box.scrollLeft = box.scrollWidth;
  }, []);

  const changeFilter = useCallback(
    <K extends keyof ReleaseMapFilters>(key: K, value: ReleaseMapFilters[K]) => {
      if (filters[key] === value) return;
      const next = { ...filters, [key]: value };
      setFilters(next);
      const url = new URL(window.location.href);
      writeReleaseMapFilters(url.searchParams, next);
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      trackLearnReleaseMapFilter({ filter: key, value });
    },
    [filters],
  );

  const rows = useMemo(() => filterReleases(valid, filters, updatedOn), [valid, filters, updatedOn]);
  const lanes = useMemo(() => orderedLanes(filters.weights, rows), [filters.weights, rows]);
  const offsets = useMemo(() => collisionOffsets(rows), [rows]);

  const layout = useMemo(() => {
    const laneY = new Map<string, number>();
    let y = TOP;
    let dividerY: number | null = null;
    lanes.forEach((l, i) => {
      if (l.openBand && dividerY === null && i > 0) {
        dividerY = y + BAND_GAP / 2;
        y += BAND_GAP;
      }
      laneY.set(l.lane, y + LANE_HEIGHT / 2);
      y += LANE_HEIGHT;
    });
    return { laneY, dividerY, height: y + 28 };
  }, [lanes]);

  const axis = useMemo(() => {
    const start = rangeStart(filters.range, updatedOn, valid);
    const latest = valid.reduce((max, r) => (r.d > max ? r.d : max), updatedOn);
    const end = parseIsoDate(latest);
    const ticks = monthTicks(start, end);
    const step = ticks.length > 12 ? 3 : 1;
    return { x: makeDateScale(start, end, PLOT_LEFT, PLOT_RIGHT), ticks, step };
  }, [filters.range, updatedOn, valid]);

  const majorLabels = useMemo(() => {
    const lastEnd: Record<string, number> = {};
    const out: { key: string; x: number; y: number; anchor: "middle" | "end"; text: string }[] = [];
    for (const r of rows) {
      if (!r.major) continue;
      const y = layout.laneY.get(r.lane);
      if (y === undefined) continue;
      const cx = axis.x(r.d);
      const text = majorLabel(r.m);
      const w = text.length * 5.6;
      const flush = cx + w / 2 > WIDTH - 4;
      const x0 = flush ? cx + 8 - w : cx - w / 2;
      const x1 = flush ? cx + 8 : cx + w / 2;
      for (const [slot, dy] of [["a", -10], ["b", 19]] as const) {
        const key = r.lane + slot;
        if (!((lastEnd[key] ?? -Infinity) > x0)) {
          out.push({ key: `${r.lane}-${r.d}-${r.m}`, x: flush ? cx + 8 : cx, y: y + dy, anchor: flush ? "end" : "middle", text });
          lastEnd[key] = x1 + 6;
          break;
        }
      }
    }
    return out;
  }, [rows, layout, axis]);

  const latest = latestReleases(rows, LATEST_COUNT);
  const typeChoices = [{ value: "all", label: "All" }, ...types.map((t) => ({ value: t, label: typeLabel(t) }))];

  return (
    <div data-testid="release-map" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5">
        <FilterGroup label="Weights" options={WEIGHTS_OPTIONS} value={filters.weights} onChange={(v) => changeFilter("weights", v)} />
        <FilterGroup label="Type" options={typeChoices} value={filters.type} onChange={(v) => changeFilter("type", v)} />
        <FilterGroup label="Range" options={RANGE_OPTIONS} value={filters.range} onChange={(v) => changeFilter("range", v)} />
      </div>

      <div ref={scrollRef} className="max-w-full overflow-x-auto rounded-md border border-gray-200 bg-white">
        <div role="group" aria-label="Timeline of AI model releases by lab" className="relative" style={{ width: WIDTH, height: layout.height }}>
          <svg aria-hidden="true" width={WIDTH} height={layout.height} viewBox={`0 0 ${WIDTH} ${layout.height}`} className="absolute inset-0">
            {axis.ticks.map((t, i) => {
              const x = axis.x(t);
              const labeled = t.getUTCMonth() % axis.step === 0;
              const firstLabeled = axis.ticks.findIndex((tt) => tt.getUTCMonth() % axis.step === 0) === i;
              return (
                <g key={t.toISOString()}>
                  <line x1={x} x2={x} y1={TOP - 6} y2={layout.height - 24} stroke={GRID} />
                  {labeled && (
                    <text x={x} y={layout.height - 8} textAnchor="middle" fontSize={10.5} fill={MUTED} className="font-mono">
                      {formatMonthTick(t, t.getUTCMonth() === 0 || firstLabeled)}
                    </text>
                  )}
                </g>
              );
            })}
            {lanes.map((l) => {
              const y = layout.laneY.get(l.lane)!;
              const color = RELEASE_LANE_COLORS[l.lane];
              return (
                <g key={l.lane}>
                  <line x1={PLOT_LEFT} x2={PLOT_RIGHT} y1={y} y2={y} stroke={LINE} strokeDasharray={l.openBand ? "2 3" : undefined} />
                  <text x={PLOT_LEFT - 12} y={y + 4} textAnchor="end" fontSize={12} fontWeight={600} fill="#2D3139">
                    {l.lane}
                  </text>
                  <circle cx={12} cy={y} r={5} fill={l.openBand ? "none" : color} stroke={color} strokeWidth={2} />
                </g>
              );
            })}
            {lanes.length > 0 && (
              <text x={20} y={TOP - 14} fontSize={10} letterSpacing="0.08em" fill={MUTED} className="font-mono">
                {lanes[0].openBand ? "OPEN WEIGHTS" : "CLOSED WEIGHTS"}
              </text>
            )}
            {layout.dividerY !== null && (
              <>
                <line x1={0} x2={WIDTH} y1={layout.dividerY} y2={layout.dividerY} stroke={MUTED} />
                <text x={20} y={layout.dividerY + 14} fontSize={10} letterSpacing="0.08em" fill={MUTED} className="font-mono">
                  OPEN WEIGHTS
                </text>
              </>
            )}
            {majorLabels.map((m) => (
              <text key={m.key} x={m.x} y={m.y} textAnchor={m.anchor} fontSize={10.5} fontWeight={600} fill="#2D3139">
                {m.text}
              </text>
            ))}
          </svg>
          {rows.map((r) => {
            const y = layout.laneY.get(r.lane);
            if (y === undefined) return null;
            const dot = r.major ? 13 : 8;
            const hit = dot + 6;
            const isSelected = selected === r;
            return (
              <button
                key={`${r.lane}-${r.d}-${r.m}`}
                type="button"
                aria-label={releaseLabel(r)}
                aria-current={isSelected ? "true" : undefined}
                title={`${r.m} (${formatReleaseDate(r.d)})`}
                onClick={() => setSelected(r)}
                onFocus={() => setSelected(r)}
                className={`absolute flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-dark ${isSelected ? "ring-2 ring-landing-dark" : ""}`}
                style={{ left: axis.x(r.d) + (offsets.get(`${r.lane}-${r.d}-${r.m}`) ?? 0) - hit / 2, top: y - hit / 2, width: hit, height: hit, zIndex: r.major ? 2 : 1 }}
              >
                <span aria-hidden="true" className="block rounded-full" style={dotStyle(r, dot)} />
              </button>
            );
          })}
        </div>
      </div>

      <ul aria-label="Legend" className="flex flex-wrap gap-x-4 gap-y-1.5 text-[13px] text-landing-secondary">
        {orderedLanes("all").map((l) => (
          <li key={l.lane} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-[11px] w-[11px] rounded-full"
              style={{ background: l.openBand ? "transparent" : RELEASE_LANE_COLORS[l.lane], border: `2px solid ${RELEASE_LANE_COLORS[l.lane]}` }}
            />
            {l.lane}
          </li>
        ))}
        <li className="inline-flex items-center gap-1.5">
          <span aria-hidden="true" className="inline-block h-[11px] w-[11px] rounded-full" style={{ border: `2px dashed ${MUTED}` }} />
          Weights announced, not yet released
        </li>
      </ul>

      <div className="grid grid-cols-1 gap-3.5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
        <section aria-label="Release details" className={PANEL}>
          {selected ? (
            <>
              <div className={EYEBROW}>
                {selected.lane} · {selected.open ? "open weights" : "closed"}
              </div>
              <h3 className="my-1 text-[19px] font-semibold text-landing-dark">{selected.m}</h3>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12px] text-landing-secondary">
                <span>{formatReleaseDate(selected.d)}</span>
                <span className="rounded-full border border-gray-200 px-2">{typeLabel(selected.mod)}</span>
                <span className="rounded-full border border-gray-200 px-2">{selected.major ? "generational" : "update"}</span>
                {selected.pending && <span className="rounded-full border border-gray-200 px-2">weights pending</span>}
              </div>
              {selected.f.length > 0 && (
                <ul className="my-2.5 list-disc space-y-1 pl-5 text-[15px] text-landing-dark">
                  {selected.f.map((fact) => (
                    <li key={fact}>{fact}</li>
                  ))}
                </ul>
              )}
              <a href={selected.src} target="_blank" rel="noopener noreferrer" className="text-[14px] font-semibold text-lev-orange underline-offset-2 hover:underline">
                Source<span className="sr-only"> for {selected.m} (opens in a new tab)</span>
              </a>
            </>
          ) : (
            <p className="text-[14px] text-landing-secondary">Select a release to see what it is and its source.</p>
          )}
        </section>
        <section aria-label={`Latest ${LATEST_COUNT} releases`} className={PANEL}>
          <div className={EYEBROW}>Latest {LATEST_COUNT} releases</div>
          <ol className="mt-2 grid gap-0.5">
            {latest.map((r) => (
              <li key={`${r.lane}-${r.d}-${r.m}`}>
                <button
                  type="button"
                  onClick={() => setSelected(r)}
                  className="grid w-full grid-cols-[96px_12px_minmax(0,1fr)] items-center gap-2 rounded px-1 py-0.5 text-left text-[14px] text-landing-dark hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-dark"
                >
                  <span className="font-mono text-[11.5px] tabular-nums text-landing-secondary">{formatShortReleaseDate(r.d)}</span>
                  <span aria-hidden="true" className="block rounded-full" style={dotStyle(r, 10)} />
                  <span className="truncate">
                    {releaseVendor(r.lane) ? <span className="font-semibold">{releaseVendor(r.lane)} · </span> : null}
                    {r.m}
                    {r.pending ? " (weights pending)" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </section>
      </div>

      <p className="text-[13.5px] text-landing-secondary">
        {countReleases(rows)} of {countReleases(valid)} releases shown. Announced models with weights still pending are drawn hollow and not counted.
        Data checked {formatReleaseDate(updatedOn)}; every entry links to its source.
      </p>

    </div>
  );
}
